export const DEACTIVATE_BALANCE_RESTRICTED_CODE = 'UA002';

export const DEACTIVATE_BALANCE_MESSAGE =
  'Para darte de baja de CriptoCocos, es necesario que vendas la totalidad de tus saldos en Bitcoin y USDC.';

export const DEACTIVATE_SUCCESS_TITLE = 'Gracias';

export const DEACTIVATE_SUCCESS_MESSAGE =
  'Tu cuenta de CriptoCocos se eliminó correctamente. Gracias por haber formado parte.';

export const DEACTIVATE_GENERIC_MESSAGE = 'Intenta nuevamente en unos minutos.';

export type DeactivateAccountModalVariant = 'confirm' | 'blocked' | 'success';

export type DeactivateBalanceWallet = {
  availableBalance?: unknown;
  accountBalance?: unknown;
};

export type DeactivateAccountFailure =
  | { kind: 'balance-restricted'; message: string }
  | { kind: 'unauthorized' }
  | { kind: 'generic'; message: string };

function parseBalance(value: unknown): number {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : 0;
  }

  if (typeof value !== 'string') {
    return 0;
  }

  const parsed = Number(value.replace(/,/g, ''));
  return Number.isFinite(parsed) ? parsed : 0;
}

/**
 * Igual que `DELETE /user/deactivate`: el backend rechaza la baja si alguna
 * wallet tiene saldo disponible o contable mayor a cero.
 */
export function walletsHaveDeactivateBlockingBalance(
  wallets: DeactivateBalanceWallet[],
): boolean {
  return wallets.some(
    (wallet) =>
      parseBalance(wallet.availableBalance) > 0 ||
      parseBalance(wallet.accountBalance) > 0,
  );
}

function readDetailMessage(detail: unknown): string | undefined {
  if (!Array.isArray(detail)) {
    return undefined;
  }

  const message = detail.find(
    (item): item is string =>
      typeof item === 'string' && item.trim().length > 0,
  );

  return message?.trim();
}

function readErrorPayload(error: unknown): {
  status: number;
  code?: string;
  description?: string;
  detailMessage?: string;
} {
  if (!error || typeof error !== 'object') {
    return { status: 0 };
  }

  const record = error as {
    status?: unknown;
    error?: unknown;
    message?: unknown;
  };
  const status = typeof record.status === 'number' ? record.status : 0;
  const body =
    record.error && typeof record.error === 'object'
      ? (record.error as {
          code?: unknown;
          description?: unknown;
          message?: unknown;
          detail?: unknown;
        })
      : undefined;

  const description =
    (typeof body?.description === 'string' && body.description.trim()) ||
    (typeof body?.message === 'string' && body.message.trim()) ||
    (typeof record.message === 'string' && record.message.trim()) ||
    undefined;

  return {
    status,
    code: typeof body?.code === 'string' ? body.code : undefined,
    description,
    detailMessage: readDetailMessage(body?.detail),
  };
}

export function resolveDeactivateAccountFailure(
  error: unknown,
): DeactivateAccountFailure {
  const info = readErrorPayload(error);
  const described = info.detailMessage || info.description || DEACTIVATE_GENERIC_MESSAGE;

  if (info.status === 401) {
    return { kind: 'unauthorized' };
  }

  const isBalanceRestriction =
    info.code === DEACTIVATE_BALANCE_RESTRICTED_CODE || info.status === 403;

  if (isBalanceRestriction) {
    const hasOtherRestrictionCode =
      Boolean(info.code) && info.code !== DEACTIVATE_BALANCE_RESTRICTED_CODE;

    return {
      kind: 'balance-restricted',
      message: hasOtherRestrictionCode
        ? info.detailMessage || info.description || DEACTIVATE_BALANCE_MESSAGE
        : DEACTIVATE_BALANCE_MESSAGE,
    };
  }

  return {
    kind: 'generic',
    message: described,
  };
}

/** `success: false` en un 200. Un body vacío sigue contando como baja hecha. */
export function deactivateResponseError(response: unknown): string | null {
  if (!response || typeof response !== 'object') {
    return null;
  }

  const body = response as {
    success?: unknown;
    message?: unknown;
    description?: unknown;
    detail?: unknown;
  };

  if (body.success !== false) {
    return null;
  }

  return (
    readDetailMessage(body.detail) ||
    (typeof body.description === 'string' && body.description.trim()) ||
    (typeof body.message === 'string' && body.message.trim()) ||
    'No se pudo dar de baja la cuenta.'
  );
}
